import { asText, Content, isFilled } from '@prismicio/client';
import { CaseStudyDocument } from '../../../../prismicio-types';
import type { CardExtras } from '../server/resolveCaseStudies';

/**
 * Read the card extras (brand logo, metric headline) that the server-side
 * `toCardDoc` attaches to `data`. Centralizes the cast so variants don't
 * reach into the untyped extension themselves.
 */
export const getCardExtras = (cs: CaseStudyDocument): CardExtras => {
  const data = cs.data as typeof cs.data & CardExtras;
  return {
    brand_logo: data.brand_logo,
    card_headline: data.card_headline,
    card_industry: data.card_industry,
  };
};

// Normalize a doc name down to just the company/brand, dropping the two
// editorial patterns docs carry: the new "Case Study [Rework] - <Brand>" prefix
// and the legacy "<Brand> - <…> Industry" suffix.
//   "Case Study Rework - LatAm Bank"     -> "LatAm Bank"
//   "Doordash - Delivery industry"       -> "Doordash"
export const cleanBrand = (s: string): string => {
  const noPrefix = s
    .trim()
    .replace(/^case\s+stud(?:y|ies)(?:\s+rework)?\s*[-–—:]\s*/i, '')
    .trim();
  // Drop a trailing " - <…> Industry" segment. Split on a SPACED dash so an
  // internal dash (e.g. "E-commerce") isn't mistaken for the separator.
  const parts = noPrefix.split(/\s+[-–—]\s+/);
  if (
    parts.length > 1 &&
    /industr(?:y|ies)\s*$/i.test(parts[parts.length - 1])
  ) {
    parts.pop();
    return parts.join(' - ').trim();
  }
  return noPrefix;
};

/** Brand label used on the card: the short `client` name, else the StructuredText `name`. */
export const brandOf = (cs: CaseStudyDocument): string => {
  const { client, name } = cs.data as Content.CaseStudyDocumentData;
  return cleanBrand((client && client.trim()) || asText(name).trim());
};

/**
 * Industry label for the card's badge. Sources, in order:
 *   1. Top-level `data.industry` field (kept as first-class in case editors
 *      ever populate it directly).
 *   2. `card_industry` — the value the server pulled from the case study's
 *      CaseStudiesAbout slice (Facts group entry with label "Industry").
 *      This is where the real data lives today.
 *   3. Legacy name pattern `<Brand> - <Industry> industry`
 *      (e.g. "Doordash - Delivery industry" → "Delivery") — a safety net
 *      for docs that predate the About slice.
 * Returns undefined when nothing is available — the badge is then simply
 * omitted rather than showing a placeholder.
 */
export const industryOf = (cs: CaseStudyDocument): string | undefined => {
  const { industry, name } = cs.data as Content.CaseStudyDocumentData;
  const declared = (industry ?? '').trim();
  if (declared) {
    return declared;
  }

  const { card_industry } = getCardExtras(cs);
  if (card_industry?.trim()) {
    return card_industry.trim();
  }

  const text = asText(name).trim();
  const parts = text.split(/\s+[-–—]\s+/);
  if (parts.length < 2) {
    return undefined;
  }
  const last = parts[parts.length - 1];
  const match = last.match(/^(.*?)\s+industr(?:y|ies)\s*$/i);
  return match ? match[1].trim() || undefined : undefined;
};

/**
 * A doc renders as a card when it has a brand/title and some content (an image
 * or a description). Redesigned docs get their image + description from the hero
 * slice (see `toCardDoc` server-side); legacy docs carry them at the top level.
 * This only skips genuinely empty stubs.
 */
const isCardComplete = (cs: CaseStudyDocument): boolean => {
  const { image, description } = cs.data as Content.CaseStudyDocumentData;
  return (
    !!brandOf(cs) && (isFilled.image(image) || !!asText(description).trim())
  );
};

/** Higher = more fields populated; used to keep the richest of two duplicates. */
const infoScore = (cs: CaseStudyDocument): number => {
  const { image, description, industry } =
    cs.data as Content.CaseStudyDocumentData;
  return (
    (isFilled.image(image) ? 1 : 0) +
    (asText(description).trim() ? 1 : 0) +
    (industry ? 1 : 0) +
    (brandOf(cs) ? 1 : 0)
  );
};

/**
 * Dedup by brand (keeping the most complete of any duplicates — e.g. legacy
 * `sightx` + `case-study-sightx` collapse to one), then drop cards that aren't
 * render-ready. Order follows each brand's first appearance (editor order).
 *
 * Shared by every data-driven variation of CaseStudiesCarousel so a stub or
 * duplicate rejected by one grid can't sneak into another.
 */
export const prepareCaseStudies = (
  caseStudies: CaseStudyDocument[],
): CaseStudyDocument[] => {
  const bestByBrand = new Map<string, CaseStudyDocument>();
  const order: string[] = [];
  for (const cs of caseStudies) {
    const key = (brandOf(cs) || cs.uid || cs.id).toLowerCase();
    const current = bestByBrand.get(key);
    if (!current) {
      bestByBrand.set(key, cs);
      order.push(key);
    } else if (infoScore(cs) > infoScore(current)) {
      bestByBrand.set(key, cs);
    }
  }
  return order.map((key) => bestByBrand.get(key)!).filter(isCardComplete);
};
